/* eslint-disable max-len */
/* eslint-disable jsx-a11y/media-has-caption */
import React, { useMemo, useEffect, useState, Fragment, CSSProperties } from 'react';
import { Card, CardContent, CardMedia, CircularProgress, makeStyles, Theme, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { capitalizeAll, GenericMessageBox } from '@moderepo/mode-app-components';
import { EntityModuleType, VideoInfo } from '@moderepo/mode-internal-apis';
import { AppAPI } from '@moderepo/mode-apis';
import { BaseEntityViewComponentProps, EntityEventViewComponentCollection } from '.';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingStringValue } from '../../../../utils/utils';
import { useSelectEntity, useSelectProject } from '../../../../utils/dataStateHooks';
import { useRitzTranslation } from '../../../../utils/customHooks';
import { getDateTimeDisplay } from '../../../containerUtils';
import { EventState, EVENT_STATE_VALUES, findMetricInfo, getEventMetricDisplayName, getEventMetricDisplayValue, isEventMetricSettings,
    EventMetricSettings } from '..';
import { CardHighlightStyle, useSelectTranslatedEntityClasses, isCardHighlightStyle } from '../..';
import { VideosScroller } from '../../../misc';
import { SettingsErrorLogger } from '../../../../utils/logger';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            '&.highlight' : {
                '&.bar': {
                    borderBottom               : '6px solid transparent',
                    [`&.${EventState.NEUTRAL}`]: {
                        borderColor: theme.palette.info.main,
                    },
                    [`&.${EventState.GOOD}`]: {
                        borderColor: theme.palette.success.main,
                    },
                    [`&.${EventState.SERIOUS}`]: {
                        borderColor: theme.palette.warning.main,
                    },
                    [`&.${EventState.CRITICAL}`]: {
                        borderColor: theme.palette.error.main,
                    },
                },
                '&.border': {
                    border                     : '6px solid transparent',
                    [`&.${EventState.NEUTRAL}`]: {
                        borderColor: theme.palette.info.main,
                    },
                    [`&.${EventState.GOOD}`]: {
                        borderColor: theme.palette.success.main,
                    },
                    [`&.${EventState.SERIOUS}`]: {
                        borderColor: theme.palette.warning.main,
                    },
                    [`&.${EventState.CRITICAL}`]: {
                        borderColor: theme.palette.error.main,
                    },
                },
                '&.background': {
                    border                     : '6px solid transparent',
                    [`&.${EventState.NEUTRAL}`]: {
                        borderColor: theme.palette.info.main,
                        background : theme.palette.info.main,
                        color      : theme.palette.info.contrastText,
                    },
                    [`&.${EventState.GOOD}`]: {
                        borderColor: theme.palette.success.main,
                        background : theme.palette.success.main,
                        color      : theme.palette.success.contrastText,
                    },
                    [`&.${EventState.SERIOUS}`]: {
                        borderColor: theme.palette.warning.main,
                        background : theme.palette.warning.main,
                        color      : theme.palette.warning.contrastText,
                    },
                    [`&.${EventState.CRITICAL}`]: {
                        borderColor: theme.palette.error.main,
                        background : theme.palette.error.main,
                        color      : theme.palette.error.contrastText,
                    },

                    // If use background color then we need to make the highlight color for the metric "value" darker
                    '& $eventValueDisplay': {
                        '& .value': {
                            '&.highlight': {
                                [`&.${EventState.NEUTRAL}`]: {
                                    background: theme.palette.info.dark,
                                },
                                [`&.${EventState.GOOD}`]: {
                                    background: theme.palette.success.dark,
                                },
                                [`&.${EventState.SERIOUS}`]: {
                                    background: theme.palette.warning.dark,
                                },
                                [`&.${EventState.CRITICAL}`]: {
                                    background: theme.palette.error.dark,
                                },
                            },
                        },
                    },
                },
            },
        },

        eventHeader: {
            width         : '100%',
            display       : 'flex',
            flexDirection : 'row',
            justifyContent: 'space-between',
            alignItems    : 'center',
            flexWrap      : 'wrap',
        },

        eventHeaderTitle: {
            flex      : 1,
            whiteSpace: 'nowrap',
        },

        eventHeaderVideoCount: {
            flex          : 1,
            display       : 'flex',
            flexDirection : 'column',
            justifyContent: 'flex-end',
            alignItems    : 'flex-end',
            textTransform : 'capitalize',
            whiteSpace    : 'nowrap',
        },

        eventMedia: {
            flex      : 1,
            width     : '100%',
            height    : '100%',
            background: theme.palette.background.default,
        },

        eventInfo: {
            width: '100%',
            flex : 1,
        },

        eventTitle: {
            textTransform: 'capitalize',
        },

        eventValueDisplay: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            '& .label'    : {
                textTransform: 'capitalize',
                display      : 'flex',
                alignItems   : 'center',
            },
            '& .value': {
                fontWeight   : 'bold',
                marginLeft   : theme.spacing(1),
                '&.highlight': {
                    borderRadius               : 20,
                    padding                    : theme.spacing(0, 1),
                    [`&.${EventState.NEUTRAL}`]: {
                        background: theme.palette.info.main,
                        color     : theme.palette.info.contrastText,
                    },
                    [`&.${EventState.GOOD}`]: {
                        background: theme.palette.success.main,
                        color     : theme.palette.success.contrastText,
                    },
                    [`&.${EventState.SERIOUS}`]: {
                        background: theme.palette.warning.main,
                        color     : theme.palette.warning.contrastText,
                    },
                    [`&.${EventState.CRITICAL}`]: {
                        background: theme.palette.error.main,
                        color     : theme.palette.error.contrastText,
                    },
                },
            },
        },

        noDataMessageComp: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
            height        : '100%',
            padding       : theme.spacing(4, 1),
        },
    };
}, { name: 'GenericVideoEventViewComp', index: 1 });



/**
 * When we specify an array of metrics to show in the event card, we need to provide some info about the metrics so that the component
 * know how to find these metrics from the metricsDefinition. We can use the 'EventMetricSettings' interface for this HOWEVER, we needed
 * additional attributes to customize the CSS style of the display values therefore, we will create a new interface 'ExtEventMetricSettings'
 * which extends EventMetricSettings
 */
interface ExtEventMetricSettings extends EventMetricSettings {

    // The CSS style to apply to the container of the label/value display
    readonly containerStyle?: CSSProperties | undefined;

    // The CSS style to apply to the label
    readonly labelStyle?: CSSProperties | undefined;

    // The CSS style to apply to the value and unit
    readonly valueStyle?: CSSProperties | undefined;
}



/**
 * Type guard function to check if an object is a type of ExtEventMetricSettings
 */
const isExtEventMetricSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is ExtEventMetricSettings => {

    const settings = obj as ExtEventMetricSettings;
    const configPath = [...(currentConfigPath ?? [])];

    // ExtEventMetricSettings extends EventMetricSettings therefore we need to call 'isEventMetricSettings' to make
    // sure it has the required attributes for a EventMetricSettings
    if (!isEventMetricSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // containerStyle and labelStyle and valueStyle are optional but if they are provided, they must be objects
    if (settings.containerStyle !== undefined && !isObjectLiteral(settings.containerStyle)) {
        return errorLogger?.logError({
            message: '"containerStyle" must be undefined or an object',
            value  : settings.containerStyle,
            path   : [...configPath, 'containerStyle'],
        }) ?? false;
    }
    if (settings.labelStyle !== undefined && !isObjectLiteral(settings.labelStyle)) {
        return errorLogger?.logError({
            message: '"labelStyle" must be undefined or an object',
            value  : settings.labelStyle,
            path   : [...configPath, 'labelStyle'],
        }) ?? false;
    }
    if (settings.valueStyle !== undefined && !isObjectLiteral(settings.valueStyle)) {
        return errorLogger?.logError({
            message: '"valueStyle" must be undefined or an object',
            value  : settings.valueStyle,
            path   : [...configPath, 'valueStyle'],
        }) ?? false;
    }

    return true;
};



/**
 * This is the interface for the GenericVideoEventViewComp's CustomSettings.
 */
export interface GenericVideoEventViewCompCustomSettings {
    // The default for these values are TRUE unless specifically set to false
    readonly showMetricsDefinitionName?: boolean | undefined;
    readonly showEventTime?: boolean | undefined;
    readonly showEntityName?: boolean | undefined;

    /**
     * To specify the list of metrics and their values we want to show in the card. The items in the array can be either just the metrics' names
     * or objects containing the metrics' names and some settings e.g. styles.
     *    customSettings: {
     *        showMetricsDefinitionName: true,
     *        showEventTime: true,
     *        // Show 2 metrics, "result", and "value". For result, there is no other settings therefore we can just specify the metricName.
     *        // For "value", because we want to customize the style therefore we provide an Object
     *        showMetrics: [
     *            "result",
     *            {
     *                name: "value",
     *                style: {
     *                    fontSize: "large",
     *                }
     *            }
     *        ]
     *    }
     */
    readonly showMetrics?: readonly (string | ExtEventMetricSettings)[] | undefined;

    /**
     * To highlight the card based on some event record field value. The card can be highlighted in different style, bar, border, or background
     */
    readonly highlightCardStyle?: CardHighlightStyle | undefined;
}



/**
 * Type guard function to validate the obj to make sure it is a type of EntityEventsUIComponentCustomSettings
 */
const isGenericVideoEventViewCompCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is GenericVideoEventViewCompCustomSettings => {
    const settings = obj as GenericVideoEventViewCompCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // showMetricsDefinitionName, showEventTime, showEntityName are optional but if they are provided, they must be booleans
    if (!isValidSettingBooleanValue(settings.showMetricsDefinitionName)) {
        return errorLogger?.logError({
            message: '"showMetricsDefinitionName" must be undefined or a boolean',
            value  : settings.showMetricsDefinitionName,
            path   : [...configPath, 'showMetricsDefinitionName'],
        }) ?? false;
    }
    if (!isValidSettingBooleanValue(settings.showEventTime)) {
        return errorLogger?.logError({
            message: '"showEventTime" must be undefined or a boolean',
            value  : settings.showEventTime,
            path   : [...configPath, 'showEventTime'],
        }) ?? false;
    }
    if (!isValidSettingBooleanValue(settings.showEntityName)) {
        return errorLogger?.logError({
            message: '"showEntityName" must be undefined or a boolean',
            value  : settings.showEntityName,
            path   : [...configPath, 'showEntityName'],
        }) ?? false;
    }
    if (settings.showMetrics !== undefined) {
        if (!(settings.showMetrics instanceof Array)) {
            return errorLogger?.logError({
                message: '"showMetrics" must be undefined or an Array',
                value  : settings.showMetrics,
                path   : [...configPath, 'showMetrics'],
            }) ?? false;
        }

        // Every item in showMetrics must be ExtEventMetricSettings
        if (!settings.showMetrics.every((metricSettings, index) => {
            if (typeof metricSettings !== 'string' && !isObjectLiteral(metricSettings)) {
                return errorLogger?.logError({
                    message: 'Value must be non-empty string or Object.',
                    value  : metricSettings,
                    path   : [...configPath, 'showMetrics', `[${index}]`],
                }) ?? false;
            }

            if (typeof metricSettings === 'string') {
                if (!isValidSettingStringValue(metricSettings, true)) {
                    // metricSettings can just be a metric name or an Object. If it is a string, we can assume it is a metric name therefore we only
                    // need to make sure it is non-empty
                    return errorLogger?.logError({
                        message: 'Value must be non-empty string.',
                        value  : metricSettings,
                        path   : [...configPath, 'showMetrics', `[${index}]`],
                    }) ?? false;
                }
                return true;
            }
            return isExtEventMetricSettings(metricSettings, errorLogger, [...configPath, 'showMetrics', `[${index}]`]);
        })) {
            return false;
        }
    }

    if (
        settings.highlightCardStyle !== undefined
        && !isCardHighlightStyle(settings.highlightCardStyle, errorLogger, [...configPath, 'highlightCardStyle'])
    ) {
        return false;
    }


    return true;
};



export interface GenericVideoEventViewCompProps extends BaseEntityViewComponentProps {
    readonly customSettings?: GenericVideoEventViewCompCustomSettings | undefined;
}


export const GenericVideoEventViewComp: React.FC<GenericVideoEventViewCompProps> = (props: GenericVideoEventViewCompProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();


    // Get the entityClasses, the entity object, and entityClass associated with the entity object
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    const selectedEntity = useSelectEntity(props.projectId, props.entityId);

    const selectedEntityClass = useMemo(() => {
        return entityClasses?.find((entityClass) => {
            return entityClass.entityClass === selectedEntity?.entityClass;
        });
    }, [entityClasses, selectedEntity?.entityClass]);

    /**
     * Get the metricsDefinition where the record came from
     */
    const selectedMetricsDefinition = useMemo(() => {
        return selectedEntityClass?.metricsDefinitions?.find((metricsDef) => {
            return metricsDef.metricsDefinitionId === props.metricsDefinitionId;
        });
    }, [props.metricsDefinitionId, selectedEntityClass?.metricsDefinitions]);



    // the selectedMetricsDefinition might have an array of 'linksTo'. One of the linksTo items might link to a VIDEO blob definition.
    // Therefore, we will loop through this selectedMetricsDefinition.linksTo array and find the item that linked to a VIDEO blob definition.
    const [linkToInfo, videoDefinition] = useMemo(() => {
        if (selectedMetricsDefinition?.linksTo && selectedEntityClass?.blobDefinitions) {
            // eslint-disable-next-line no-restricted-syntax
            for (const linkTo of selectedMetricsDefinition?.linksTo) {

                // Get the linkTo.blobDefinitionId and then find the blob definition that matches the blobDefinitionId AND the blob
                // moduleType is a VIDEO type. We only care about the blobDefinition of type VIDEO.
                const blobDefId = linkTo.blobDefinitionId;
                const blobDef = selectedEntityClass.blobDefinitions.find((blobDef) => {
                    return blobDef.blobDefinitionId === blobDefId && blobDef.dataStoreConfiguration.moduleType === EntityModuleType.VIDEO;
                });

                // If we found a blob definition then return it, no need to go through all the linksTo
                if (blobDef) {
                    return [linkTo, blobDef];
                }
            }
        }
        return [undefined, undefined];
    }, [selectedEntityClass?.blobDefinitions, selectedMetricsDefinition?.linksTo]);


    // The videos tied to the event
    const [eventVideos, setEventVideos] = useState<readonly VideoInfo[]>();
    const [currentVideo, setCurrentVideo] = useState<number>(0);


    useEffect(() => {
        // If there is a linkToInfo and blob definition for the video, try to fetch the video
        if (selectedEntity && linkToInfo && videoDefinition) {
            // linkTo has an attribute 'keyField' which specifies which field in the record that contain the value to be used
            // for the video search key. NOTE: this field can be at the top level of the record object OR inside the record.fields.
            // We will try to get it from the top level first and if not there, we look inside record.fields.
            const searchKey = props.record[linkToInfo.keyField] ?? props.record.fields[linkToInfo.keyField];
            AppAPI.getInstance().getHomeVideos(
                selectedEntity.homeId,
                videoDefinition.dataStoreConfiguration.moduleInstanceId,
                {
                    searchKeys: searchKey,
                },
            ).then((videos) => {
                if (videos && videos.length > 0) {
                    setEventVideos(videos);
                    setCurrentVideo(0);
                }
            });
        }
    }, [linkToInfo, props.record, selectedEntity, videoDefinition]);



    // Just some flag we need to determine if we need to add the container to display header and main content
    const hasCardHeaderContent = props.customSettings?.showEventTime !== false || (eventVideos && eventVideos.length > 0);
    const hasCardMainContent = props.customSettings?.showMetricsDefinitionName !== false || props.customSettings?.showEntityName !== false
      || (props.customSettings.showMetrics && props.customSettings.showMetrics.length > 0);



    /**
     * Get the Card highlight style from settings and get the event's state based on the state of each of the visible metrics.
     */
    const { highlightCardStyle, eventState } = useMemo(() => {
        const highlightCardStyle = props.customSettings?.highlightCardStyle;

        // If we need to highlight the card AND if there are 'showMetrics' settings then use the `showMetrics` settings to
        // get the state of the event. The state of the event is the WORST state among the state of each metric in the 'showMetrics' array.
        // For example, if the 'showMetrics' has a couple of states and one of the metric has GOOD state and another has CRITICAL state
        // then the event's state will be CRITICAL.
        if (highlightCardStyle && props.customSettings.showMetrics) {

            const eventState = props.customSettings.showMetrics.reduce((worstState: EventState | undefined, metricSettings): EventState | undefined => {
                // Get the valueToState settings for this current metric. Not all metric has this settings. If this metric does not have
                // this settings then ignore this metric. This metric's value SHOULD NOW affect the event's state.
                const metricSettingsObject = isExtEventMetricSettings(metricSettings) ? metricSettings : undefined;
                if (metricSettingsObject?.valueToEventStateMapping) {
                    // This metric has 'valueToEventStateMapping' so check to see what the state for this metric is based on the event's value
                    // for this metric
                    const value = props.record.fields[metricSettingsObject.name];
                    const metricState = metricSettingsObject.valueToEventStateMapping[value ?? ''];
                    if (metricState && (worstState === undefined || EVENT_STATE_VALUES[metricState] > EVENT_STATE_VALUES[worstState])) {
                        // If this metric has state AND if we have not found the worstState yet OR if the current worst state is not as bad
                        // as this metrics state then return this metric's state as the worst.
                        return metricState;
                    }
                }

                // Return the current worstState
                return worstState;
            }, undefined);

            return {
                highlightCardStyle, eventState,
            };
        }

        return {
            highlightCardStyle: undefined, eventState: undefined,
        };
    }, [props.customSettings?.highlightCardStyle, props.customSettings?.showMetrics, props.record.fields]);


    return (
        <>
            {selectedEntity && (
                <Card
                    className={clsx(
                        props.className, classes.root,
                        highlightCardStyle && 'highlight',
                        highlightCardStyle,
                        eventState,
                    )}
                    style={props.style}
                    onClick={props.onClick}
                >
                    {hasCardHeaderContent && (
                        <CardContent
                            className={classes.eventHeader}
                        >
                            {props.customSettings?.showEventTime !== false && (
                                <Typography
                                    className={classes.eventHeaderTitle}
                                    variant="subtitle1"
                                >
                                    {getDateTimeDisplay(props.record.timestamp, trans)}
                                </Typography>
                            )}
                            {eventVideos && eventVideos.length > 1 && (
                                <div className={classes.eventHeaderVideoCount}>
                                    {trans('label.video')}: {currentVideo + 1}/{eventVideos.length}
                                </div>
                            )}
                        </CardContent>
                    )}
                    <CardMedia className={classes.eventMedia}>
                        <VideosScroller
                            videos={eventVideos}
                            noDataMessageComp={(
                                <GenericMessageBox
                                    className={classes.noDataMessageComp}
                                    icon="videocam_off"
                                    title={capitalizeAll(trans('generic.no_data.title', { term: trans('label.video', { count: 2 }) }))}
                                />
                            )}
                        />
                    </CardMedia>

                    {hasCardMainContent && (
                        <CardContent
                            className={classes.eventInfo}
                        >
                            {props.customSettings?.showMetricsDefinitionName !== false && (
                                <Typography
                                    className={classes.eventTitle}
                                    variant="h6"
                                >
                                    {selectedMetricsDefinition?.displayName ?? selectedMetricsDefinition?.metricsDefinitionId}
                                </Typography>
                            )}
                            {props.customSettings?.showEntityName !== false && (
                                <Typography
                                    className={classes.eventValueDisplay}
                                    variant="subtitle1"
                                >
                                    <span className="label">
                                        {trans('label.name')}:
                                    </span>
                                    <span className="value">
                                        {selectedEntity.displaySettings.name}
                                    </span>
                                </Typography>
                            )}
                            {selectedMetricsDefinition && props.customSettings?.showMetrics?.map((metricSettings) => {

                                // metricSettings can be a string or an object. If it is a string, that string is the name of the metric we need to show.
                                // We can create a NEW ExtEventMetricSettings object using that value as the metric name. If 'metricSettings' is an object
                                // then we can use it as is, no need to do anything.
                                const metricSettingsObj: ExtEventMetricSettings = typeof metricSettings === 'string'
                                    ? {
                                        name: metricSettings,
                                    } : metricSettings;

                                const metric = findMetricInfo(selectedMetricsDefinition, metricSettingsObj);

                                if (metric) {
                                    const displayName = getEventMetricDisplayName(metric, metricSettingsObj, trans);
                                    const value = props.record.fields[metric.name];

                                    // Get the display value for the value based on the metric and metricSettingsObj
                                    const displayValue = getEventMetricDisplayValue(value, metric, metricSettingsObj, trans);

                                    // Get the state of this metric based on the metric value and the valueToEventStateMapping
                                    const metricValueToStateSettings = metricSettingsObj.valueToEventStateMapping;
                                    const metricState = metricValueToStateSettings ? metricValueToStateSettings[value ?? ''] : undefined;

                                    return (
                                        <Typography
                                            key={`${metricSettingsObj.name}-${metricSettingsObj.valueType}`}
                                            className={classes.eventValueDisplay}
                                            variant="subtitle1"
                                            style={metricSettingsObj.containerStyle}
                                        >
                                            <span className="label" style={metricSettingsObj.labelStyle}>
                                                {displayName}:
                                            </span>
                                            <span
                                                className={clsx(
                                                    'value',
                                                    metricState && 'highlight',
                                                    metricState,
                                                )}
                                                style={metricSettingsObj.valueStyle}
                                            >
                                                {value !== undefined ? (
                                                    displayValue
                                                ) : (
                                                    capitalizeAll(trans('label.none'))
                                                )}
                                            </span>
                                        </Typography>
                                    );
                                }
                                return (
                                    <Fragment key={`${metricSettingsObj.name}-${metricSettingsObj.valueType}`} />
                                );
                            })}
                        </CardContent>
                    )}
                </Card>
            )}

            {!selectedEntity && (
                <CircularProgress />
            )}
        </>

    );
};


// Register the adapter with the EntityUIComponentAdapterCollection
EntityEventViewComponentCollection.getInstance().registerComponent(
    'Generic_Video_Event_View', {
        componentFunc          : GenericVideoEventViewComp,
        customSettingsValidator: isGenericVideoEventViewCompCustomSettings,
    },
);
